import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { UserCheck, RefreshCw, Loader2, Mail, Building2, AlertTriangle, CheckCircle2, XCircle, Clock, Users, Briefcase, MessageSquare, ChevronDown, ChevronUp, Check, X, Timer, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const STATUS_META = {
  pending_approval: { color: 'bg-amber-500/15 text-amber-300 border-amber-500/30', label: 'Pending', Icon: Clock },
  active:           { color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', label: 'Approved', Icon: CheckCircle2 },
  declined:         { color: 'bg-rose-500/15 text-rose-300 border-rose-500/30', label: 'Declined', Icon: XCircle },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending_approval;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-medium ${meta.color}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function fmtDuration(seconds) {
  if (seconds == null || isNaN(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = seconds / 60;
  if (m < 60) return `${Math.round(m)}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(h < 10 ? 1 : 0)}h`;
  const d = h / 24;
  return `${d.toFixed(d < 10 ? 1 : 0)}d`;
}

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return ''; }
}

export default function SignupRequestsPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({});
  const [declineDialog, setDeclineDialog] = useState(null); // { user_id, email, reason }
  const [stats, setStats] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter === 'all' ? '' : `status=${filter}`;
      const [data, statsRes] = await Promise.all([
        api.listSignupRequests(qs),
        api.signupRequestStats().catch(() => null),
      ]);
      setRequests(Array.isArray(data) ? data : []);
      if (statsRes) setStats(statsRes);
    } catch (err) {
      toast.error(err.message || 'Could not load signup requests');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { if (isSuperAdmin) load(); else setLoading(false); }, [load, isSuperAdmin]);

  const handleApprove = async (userId) => {
    setBusy(b => ({ ...b, [userId]: 'approve' }));
    try {
      const res = await api.approveSignupRequest(userId);
      toast.success(`Approved — trial ends ${new Date(res.trial_ends_at).toLocaleDateString()}`);
      await load();
    } catch (err) {
      toast.error(err.message || 'Approve failed');
    } finally {
      setBusy(b => { const c = { ...b }; delete c[userId]; return c; });
    }
  };

  const handleDeclineSubmit = async () => {
    if (!declineDialog) return;
    const { user_id, reason } = declineDialog;
    setBusy(b => ({ ...b, [user_id]: 'decline' }));
    try {
      await api.declineSignupRequest(user_id, reason);
      toast.success('Request declined');
      setDeclineDialog(null);
      await load();
    } catch (err) {
      toast.error(err.message || 'Decline failed');
    } finally {
      setBusy(b => { const c = { ...b }; delete c[user_id]; return c; });
    }
  };

  const counts = requests.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  const totalPending = counts.pending_approval || 0;

  if (!isSuperAdmin) {
    return (
      <div className="p-8" data-testid="signup-requests-forbidden">
        <div className="max-w-md mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/10 p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-white mb-1">Super admin only</h2>
          <p className="text-sm text-zinc-300">Only super admins can review signup requests.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="signup-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-white flex items-center gap-2">
            <UserCheck className="w-6 h-6 text-blue-400" />
            Signup Requests
            {totalPending > 0 && filter !== 'pending' && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/30 text-xs font-medium" data-testid="pending-count-pill">
                {totalPending} pending
              </span>
            )}
          </h1>
          <p className="text-sm text-zinc-400 mt-1">Review prospects who signed up without an invite code. Approve to start their 14-day trial.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[160px] bg-white/5 border-white/10 text-white rounded-xl" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-white/10 text-white">
              <SelectItem value="pending" data-testid="filter-pending">Pending</SelectItem>
              <SelectItem value="approved" data-testid="filter-approved">Approved</SelectItem>
              <SelectItem value="declined" data-testid="filter-declined">Declined</SelectItem>
              <SelectItem value="all" data-testid="filter-all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} className="rounded-xl bg-white/5 border-white/10 text-zinc-300" data-testid="refresh-requests-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="signup-stats-grid">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</div>
          <div className="text-2xl font-semibold text-amber-300 mt-1" data-testid="count-pending">{counts.pending_approval || 0}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Approved</div>
          <div className="text-2xl font-semibold text-emerald-300 mt-1" data-testid="count-approved">{counts.active || 0}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><XCircle className="w-3 h-3" /> Declined</div>
          <div className="text-2xl font-semibold text-rose-300 mt-1" data-testid="count-declined">{counts.declined || 0}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="stat-median-time">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Timer className="w-3 h-3" /> Median time-to-approve</div>
          <div className="text-2xl font-semibold text-blue-300 mt-1">{fmtDuration(stats?.median_time_to_approve_seconds)}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">across {stats?.median_time_to_approve_sample_size || 0} approvals</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="stat-channels">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Zap className="w-3 h-3" /> Slack vs Web</div>
          {stats && stats.approval_channels.total > 0 ? (
            <>
              <div className="mt-1.5 flex h-2 rounded-full overflow-hidden bg-white/5">
                <div className="bg-fuchsia-400/80" style={{ width: `${stats.approval_channels.slack_pct}%` }} title={`Slack ${stats.approval_channels.slack_pct}%`} />
                <div className="bg-sky-400/80" style={{ width: `${stats.approval_channels.web_pct}%` }} title={`Web ${stats.approval_channels.web_pct}%`} />
              </div>
              <div className="flex items-center justify-between text-[10px] mt-1.5">
                <span className="text-fuchsia-300" data-testid="stat-slack-pct">{stats.approval_channels.slack_pct}% Slack ({stats.approval_channels.slack})</span>
                <span className="text-sky-300" data-testid="stat-web-pct">{stats.approval_channels.web_pct}% web ({stats.approval_channels.web})</span>
              </div>
            </>
          ) : (
            <div className="text-2xl font-semibold text-zinc-500 mt-1">—</div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-zinc-400" data-testid="requests-loading">
            <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />Loading…
          </div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center text-zinc-500" data-testid="requests-empty">
            No {filter === 'all' ? '' : filter} signup requests.
            {filter === 'pending' && <p className="text-xs mt-2 text-zinc-600">New no-invite signups will appear here for review.</p>}
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {requests.map(r => {
              const isOpen = !!expanded[r.user_id];
              const isPending = r.status === 'pending_approval';
              return (
                <div key={r.user_id} data-testid={`request-row-${r.user_id}`}>
                  <button
                    onClick={() => setExpanded(e => ({ ...e, [r.user_id]: !isOpen }))}
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left"
                    data-testid={`toggle-${r.user_id}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <StatusBadge status={r.status} />
                      <div className="min-w-0">
                        <div className="text-sm text-white font-medium truncate">{r.name || r.email}</div>
                        <div className="text-xs text-zinc-500 truncate">{r.email}{r.company ? ` · ${r.company}` : ''}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <span className="text-xs text-zinc-500 hidden sm:inline" data-testid={`time-ago-${r.user_id}`}>{timeAgo(r.created_at)}</span>
                      {!r.email_verified && isPending && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-500/15 text-zinc-400 border border-zinc-500/30">unverified</span>
                      )}
                      {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-5 bg-white/[0.01]" data-testid={`expanded-${r.user_id}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-zinc-300 mb-4">
                        <div className="flex items-start gap-2"><Mail className="w-3.5 h-3.5 text-zinc-500 mt-0.5" /><div><div className="text-zinc-500 text-[10px] uppercase tracking-wider">Email</div>{r.email} {r.email_verified ? <span className="text-emerald-400 ml-1">(verified)</span> : <span className="text-amber-400 ml-1">(awaiting verification)</span>}</div></div>
                        <div className="flex items-start gap-2"><Building2 className="w-3.5 h-3.5 text-zinc-500 mt-0.5" /><div><div className="text-zinc-500 text-[10px] uppercase tracking-wider">Company</div>{r.company || r.tenant_name || '—'}</div></div>
                        <div className="flex items-start gap-2"><Briefcase className="w-3.5 h-3.5 text-zinc-500 mt-0.5" /><div><div className="text-zinc-500 text-[10px] uppercase tracking-wider">Role</div>{r.role_title || '—'}</div></div>
                        <div className="flex items-start gap-2"><Users className="w-3.5 h-3.5 text-zinc-500 mt-0.5" /><div><div className="text-zinc-500 text-[10px] uppercase tracking-wider">Team size</div>{r.team_size || '—'}</div></div>
                        <div className="flex items-start gap-2 md:col-span-2"><MessageSquare className="w-3.5 h-3.5 text-zinc-500 mt-0.5" /><div className="flex-1"><div className="text-zinc-500 text-[10px] uppercase tracking-wider">Use case</div>{r.use_case || '—'}</div></div>
                        <div className="text-zinc-500 text-[11px] md:col-span-2">Signed up {fmtTime(r.created_at)}{r.approved_at ? ` · Approved ${fmtTime(r.approved_at)} by ${r.approved_by}` : ''}{r.declined_at ? ` · Declined ${fmtTime(r.declined_at)} by ${r.declined_by}` : ''}</div>
                        {r.decline_reason && (
                          <div className="md:col-span-2 rounded-lg bg-rose-500/10 border border-rose-500/20 p-2 text-rose-300">
                            <span className="text-[10px] uppercase tracking-wider">Internal note:</span> {r.decline_reason}
                          </div>
                        )}
                      </div>

                      {isPending && (
                        <div className="flex items-center gap-2 pt-3 border-t border-white/5">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(r.user_id)}
                            disabled={!!busy[r.user_id]}
                            className="rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white"
                            data-testid={`approve-${r.user_id}`}
                          >
                            {busy[r.user_id] === 'approve' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                            Approve & start trial
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeclineDialog({ user_id: r.user_id, email: r.email, reason: '' })}
                            disabled={!!busy[r.user_id]}
                            className="rounded-xl bg-white/5 border-rose-500/30 text-rose-300 hover:bg-rose-500/10"
                            data-testid={`decline-${r.user_id}`}
                          >
                            <X className="w-3.5 h-3.5 mr-1" /> Decline
                          </Button>
                          {!r.email_verified && (
                            <span className="text-[11px] text-amber-400 ml-2">Email still unverified — approving will start their trial anyway.</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!declineDialog} onOpenChange={(o) => { if (!o) setDeclineDialog(null); }}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white sm:max-w-md" data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Decline signup request</DialogTitle>
            <DialogDescription className="text-zinc-400">
              We&apos;ll email <span className="text-white">{declineDialog?.email}</span> a polite decline. The reason below is an internal note — it won&apos;t be shared with them.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="decline-reason"
            placeholder="e.g. Competitor fishing trip / Not in target market / Existing customer account"
            value={declineDialog?.reason || ''}
            onChange={e => setDeclineDialog(d => ({ ...d, reason: e.target.value }))}
            className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl min-h-[80px] resize-none"
            maxLength={500}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeclineDialog(null)} className="rounded-xl bg-white/5 border-white/10 text-zinc-300" data-testid="decline-cancel">Cancel</Button>
            <Button
              onClick={handleDeclineSubmit}
              disabled={!!busy[declineDialog?.user_id]}
              className="rounded-xl bg-rose-500 hover:bg-rose-600 text-white"
              data-testid="decline-confirm"
            >
              {busy[declineDialog?.user_id] === 'decline' ? 'Declining…' : 'Decline request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
